import React, { useMemo, useState } from 'react'
import { Menu as MantineMenu } from '@mantine/core'
import { Box, Button, Divider, GlobalStyles, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack } from '@mui/material'
import { ComponentsContext, FormattingToolbar, useComponentsContext } from '@blocknote/react'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import CheckIcon from '@mui/icons-material/Check'
import { useSmartTranslation } from '../hooks/useSmartTranslation'

const MENU_BACKGROUND_COLOR = '#1f1f1f'

const PortalMenuRoot = ({ children, onOpenChange, position, sub }) => {
  const menuStyles = {
    dropdown: {
      backgroundColor: MENU_BACKGROUND_COLOR,
      border: '1px solid rgba(255, 255, 255, 0.12)',
      minWidth: '12rem',
      maxHeight: 'min(60vh, 24rem)',
      overflowY: 'auto',
      padding: '0.25rem',
      zIndex: 3000
    },
    item: {
      color: '#f5f5f5',
      fontSize: '0.95rem',
      lineHeight: 1.2,
      minHeight: '2rem',
      padding: '0.4rem 0.65rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    itemLabel: {
      color: '#f5f5f5'
    },
    itemSection: {
      color: '#f5f5f5',
      width: '1rem',
      minWidth: '1rem',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    label: {
      color: '#bdbdbd'
    }
  }

  if (sub) {
    return (
      <MantineMenu.Sub
        transitionProps={{ duration: 250, exitDelay: 250 }}
        withinPortal
        middlewares={{ flip: true, shift: true, inline: false, size: true }}
        onChange={onOpenChange}
        position={position}
        styles={menuStyles}
        zIndex={3000}
      >
        {children}
      </MantineMenu.Sub>
    )
  }

  return (
    <MantineMenu
      withinPortal
      middlewares={{ flip: true, shift: true, inline: false, size: true }}
      onChange={onOpenChange}
      position={position}
      returnFocus={false}
      styles={menuStyles}
      zIndex={3000}
    >
      {children}
    </MantineMenu>
  )
}

const colorOptionStyles = {
  default: '#f5f5f5',
  gray: '#9e9e9e',
  brown: '#8d6e63',
  red: '#ef5350',
  orange: '#fb8c00',
  yellow: '#fdd835',
  green: '#66bb6a',
  blue: '#42a5f5',
  purple: '#ab47bc',
  pink: '#ec407a'
}

const ColorMenuStyles = () => (
  <GlobalStyles
    styles={Object.fromEntries(
      Object.entries(colorOptionStyles).flatMap(([colorName, colorValue]) => ([
        [
          `[data-test="text-color-${colorName}"], [data-test="background-color-${colorName}"]`,
          { color: `${colorValue} !important` }
        ],
        [
          `[data-test="text-color-${colorName}"] *, [data-test="background-color-${colorName}"] *`,
          { color: `${colorValue} !important` }
        ]
      ]))
    )}
  />
)

const ToolbarSelect = ({ className, items, isDisabled }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const selectedItem = items.find((item) => item.isSelected)

  if (!selectedItem) {
    return null
  }

  return (
    <>
      <Button
        className={className}
        disabled={isDisabled}
        size='small'
        color='inherit'
        onClick={(event) => setAnchorEl(event.currentTarget)}
        startIcon={selectedItem.icon}
        endIcon={anchorEl ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
        sx={{
          backgroundColor: MENU_BACKGROUND_COLOR,
          color: 'inherit',
          minWidth: 'fit-content',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: MENU_BACKGROUND_COLOR
          }
        }}
      >
        {selectedItem.text}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        disableScrollLock
        PaperProps={{
          sx: {
            backgroundColor: MENU_BACKGROUND_COLOR,
            color: '#f5f5f5',
            border: '1px solid rgba(255, 255, 255, 0.12)'
          }
        }}
        MenuListProps={{
          dense: true,
          sx: {
            backgroundColor: MENU_BACKGROUND_COLOR
          }
        }}
      >
        {items.map((item) => (
          <MenuItem
            key={item.text}
            disabled={item.isDisabled}
            selected={item.isSelected}
            sx={{
              color: '#f5f5f5'
            }}
            onClick={() => {
              item.onClick()
              setAnchorEl(null)
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{item.text}</ListItemText>
            {item.isSelected ? <CheckIcon fontSize='small' /> : null}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

const FormattingToolbarWithPortalSelect = ({ editor }) => {
  const components = useComponentsContext()
  const customComponents = useMemo(() => {
    if (!components) {
      return components
    }

    return {
      ...components,
      FormattingToolbar: {
        ...components.FormattingToolbar,
        Select: ToolbarSelect
      },
      Generic: {
        ...components.Generic,
        Menu: {
          ...components.Generic.Menu,
          Root: PortalMenuRoot
        },
        Toolbar: {
          ...components.Generic.Toolbar,
          Select: ToolbarSelect
        }
      },
      LinkToolbar: {
        ...components.LinkToolbar,
        Select: ToolbarSelect
      }
    }
  }, [components])

  if (!customComponents) {
    return <FormattingToolbar editor={editor} />
  }

  return (
    <ComponentsContext.Provider value={customComponents}>
      <FormattingToolbar editor={editor} />
    </ComponentsContext.Provider>
  )
}

const BlockNoteHeader = ({ editor }) => {
  const [showMenu, setShowMenu] = useState(null)
  const { t_ } = useSmartTranslation()
  const emojis = [
    '⚫', '⚪', '🔵', '🔴', '🟠', '🟡', '🟢',
    '🛑', '⚠️', '⛔', '🚫', '🔒', '🔓', '🚦',
    '⚡', '🔥', '🧪', '❄️', '☢️', '✅', '❌',
    '⚙️', '🔧', '🛠️', '📏', '🏷️',
    '🔍', '👁️', '⏱️', '📋', '▶️'
  ]

  return (
    <>
      <ColorMenuStyles />
      <Stack>
        <Stack
          direction='row'
          alignItems='center'
          gap={1}
          sx={{
            padding: '0.2rem',
            backgroundColor: MENU_BACKGROUND_COLOR
          }}
        >
          <FormattingToolbarWithPortalSelect editor={editor} />

          <div style={{ position: 'relative' }}>
            <Button
              color='primary'
              onClick={(e) => setShowMenu(e.target)}
              endIcon={showMenu ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              sx={{
                backgroundColor: MENU_BACKGROUND_COLOR,
                '&:hover': {
                  backgroundColor: MENU_BACKGROUND_COLOR
                }
              }}
            >
              {t_('Add icon')}
            </Button>

            <Menu
              open={showMenu}
              onClose={() => setShowMenu(false)}
              anchorEl={showMenu}
              PaperProps={{
                sx: {
                  backgroundColor: MENU_BACKGROUND_COLOR,
                  color: '#f5f5f5',
                  border: '1px solid rgba(255, 255, 255, 0.12)'
                }
              }}
              MenuListProps={{
                sx: {
                  backgroundColor: MENU_BACKGROUND_COLOR
                }
              }}
            >
              <Box
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gap: '0.5rem',
                  padding: '0.5rem'
                }}
              >
                {emojis.map((item) => (
                  <IconButton
                    key={item}
                    onClick={() => {
                      editor.insertInlineContent([{ type: 'text', text: `${item}`, styles: {} }])
                      setShowMenu(false)
                      editor.focus()
                    }}
                  >
                    {item}
                  </IconButton>
                ))}
              </Box>
            </Menu>
          </div>
        </Stack>
        <Divider />
      </Stack>
    </>
  )
}

export { MENU_BACKGROUND_COLOR }
export default BlockNoteHeader
